import type { MyWorkspace, OnboardingStatus, WorkspaceContext } from "@closer/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { api, ApiRequestError } from "./apiClient";
import type { SafeWorkspaceDecision } from "./safeWorkspace";
import { clearTenantQueries, tenantKey } from "./tenantQuery";
import { clearThemeCache } from "./theme";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  /**
   * Sunucudan gelir; istemci hesaplamaz (CLOS-359). Arayüz bu değere bakarak yalnız
   * YÖNLENDİRME yapar — erişim kararı sunucudaki `requireVerifiedEmail` guard'ındadır.
   * İstemcinin bu alanı değiştirmesi hiçbir kapıyı açmaz.
   */
  emailVerified: boolean;
  /**
   * İlk giriş onboarding durumu (CLOS-224 `[047]`). Oturum yükünde taşınıyor: guard ilk
   * render'da karar verebilmeli, ayrı bir istek beklemek her açılışta titreme üretirdi.
   */
  onboardingStatus: OnboardingStatus;
}

/**
 * `GET /api/tenants/mine` satırı — tip artık PAYLAŞILAN şemadan geliyor (CLOS-79 `[042]`).
 *
 * Önceden burada elle bir `interface` duruyordu ve sunucunun döndürdüğü `type` alanını
 * sessizce düşürüyordu. Ölçülen sonucu: kişisel çalışma alanı, ekranda organizasyonlardan
 * ayırt edilemeden kullanıcının kendi adıyla "Firma Seç" listesinde görünüyordu. Elle
 * tutulan bir kopya, sunucu sözleşmesi büyüdüğünde sessizce geride kalır.
 */
export type Membership = MyWorkspace;

export interface LoginInput {
  email: string;
  password: string;
}

export interface RegisterInput {
  name: string;
  email: string;
  password: string;
  /**
   * Kabul edilen belge SÜRÜMLERİNİN kimlikleri (CLOS-342). Sunucu bunların yürürlükteki
   * zorunlu metinleri tam kapsadığını doğrular; istemcinin iddiası tek başına yetmez.
   */
  acceptedDocumentIds: string[];
}

interface AuthContextValue {
  user: AuthUser | null;
  memberships: Membership[];
  isLoading: boolean;
  /** Üyelik listesi henüz yükleniyor mu — guard'lar erken karar vermesin diye. */
  membershipsLoading: boolean;
  /** Üyelik sorgusu HATA verdi mi — boş liste ile ağ hatası karışmasın diye. */
  membershipsError: boolean;
  activeTenantId: string | null;
  activeTenant: Membership | null;
  /**
   * Aktif alanın sunucudan gelen bağlamı: rol adı ve etkin yetkiler (CLOS-80 `[043]`).
   * Alan seçili değilken `null`.
   */
  activeContext: WorkspaceContext | null;
  /**
   * Etkin yetki sorgusu — menü ve düğme GÖRÜNÜRLÜĞÜ için.
   *
   * ⚠ Bu bir GÜVENLİK SINIRI DEĞİL, bir kolaylıktır. Gerçek kapı sunucudaki
   * `requirePermission`; burada `true` dönmesi hiçbir isteği geçirmez.
   */
  can: (permission: string) => boolean;
  /**
   * ⚠ İKİNCİ PARAMETRE SEÇİMİN SAHİBİ: hatırlanan alanın yanına onu seçen kullanıcının
   * kimliği yazılıyor (ortak bilgisayar koruması, bkz. `ACTIVE_TENANT_OWNER_KEY`).
   */
  setActiveTenant: (tenantId: string | null, ownerId?: string | null) => void;
  /**
   * Güvenli bağlam değiştirme (CLOS-80 `[043]`).
   *
   * Sırayla: hedefte erişim DOĞRULANIR → önceki alanın verisi bellekten atılır → bağlam
   * yazılır. Doğrulama düşerse hiçbiri olmaz ve kullanıcı mevcut alanında kalır — kartın
   * *"geçiş sırasında hata güvenli geri dönüş sağlar"* kriteri.
   */
  switchWorkspace: (tenantId: string) => Promise<WorkspaceContext>;
  /**
   * Erişimi kaybedilmiş alandan güvenli geri dönüş (CLOS-81 `[044]`): eski alanın verisi
   * atılır, yedek bağlam yazılır ve kullanıcıya sebebi açıklanır.
   */
  recoverWorkspace: (karar: Extract<SafeWorkspaceDecision, { tur: "yedek" }>) => void;
  /** Oturum ve üyelik sorgularını tazeler — onboarding gibi oturum yükünü değiştiren akışlar için. */
  refreshSession: () => Promise<void>;
  login: (input: LoginInput) => Promise<LoginSonucu>;
  completeTwoFactor: (input: {
    challenge: string;
    code?: string;
    recoveryCode?: string;
  }) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
}

/**
 * Rota sabitleri — backend path'i değişirse YALNIZ burası güncellenir.
 * Kanonik gateway: /api/session*; ham BetterAuth /api/auth/* rotaları KULLANILMAZ.
 */
/** Girişin iki olası sonucu: oturum açıldı ya da ikinci adım gerekiyor (`[018]`). */
export type LoginSonucu = { durum: "acildi" } | { durum: "ikinci-adim"; challenge: string };

export const AUTH_ROUTES = {
  session: "/api/session",
  login: "/api/session/login",
  twoFactor: "/api/session/two-factor",
  twoFactorRecovery: "/api/session/two-factor/recovery",
  register: "/api/session/register",
  logout: "/api/session",
  memberships: "/api/tenants/mine",
} as const;

const ACTIVE_TENANT_STORAGE_KEY = "activeTenantId";

/**
 * SEÇİMİN SAHİBİ (saha bulgusu, 5 Eylül 2026).
 *
 * ### ⚠ Sorun: iki organizasyonu olan kullanıcı HER GİRİŞTE seçim ekranı görüyordu
 *
 * Çıkış, hatırlanan alanı siliyordu; gerekçesi ortak bir bilgisayarda bir sonraki
 * kullanıcının öncekinin alanına düşmemesiydi (CLOS-105 `[066]` ile aynı sınıf). Doğru
 * bir kaygı, ama bedeli yanlış kişiye ödetiliyordu: tek organizasyonu olan kullanıcı
 * `safeWorkspace.ts`'teki kendiliğinden seçimle kurtuluyor, İKİ ya da daha fazla
 * organizasyonu olan her girişte aynı formu dolduruyordu.
 *
 * ### ⚠ Çözüm: seçimi silmek yerine SAHİBİYLE birlikte yazmak
 *
 * Hatırlanan alanın yanına onu seçen kullanıcının kimliği yazılıyor. Oturum yüklendiğinde
 * kimlik eşleşmiyorsa seçim kullanılmıyor ve siliniyor — yani ortak bilgisayar koruması
 * AYNEN duruyor, çünkü korunan şey "çıkışta silmek" değil "başkasının seçimine düşmemek".
 * Eşleşiyorsa kullanıcı doğrudan bıraktığı yerden devam ediyor.
 *
 * ⚠ Bu bir ERİŞİM KARARI DEĞİL: hatırlanan kimlik yalnız bir başlangıç noktası. `RequireTenant`
 * her açılışta üyeliği ve `selectable` durumunu sunucudan yeniden değerlendiriyor; askıya
 * alınmış ya da kaybedilmiş bir alan hatırlansa bile güvenli alana düşülüyor.
 */
const ACTIVE_TENANT_OWNER_KEY = "activeTenantOwnerId";

function hatirlananSecim(): { tenantId: string | null; ownerId: string | null } {
  try {
    return {
      tenantId: localStorage.getItem(ACTIVE_TENANT_STORAGE_KEY),
      ownerId: localStorage.getItem(ACTIVE_TENANT_OWNER_KEY),
    };
  } catch {
    /** Depolama kapalıysa (gizli sekme, site verisi engelli) seçim hatırlanmaz; ürün çalışır. */
    return { tenantId: null, ownerId: null };
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [activeTenantId, setActiveTenantId] = useState<string | null>(
    () => hatirlananSecim().tenantId,
  );

  const sessionQuery = useQuery({
    queryKey: ["session"],
    retry: false,
    queryFn: async (): Promise<{ user: AuthUser | null }> => {
      try {
        return (await api<{ user: AuthUser | null }>(AUTH_ROUTES.session)).data;
      } catch (error) {
        // Oturum yoksa 401 normaldir; hata değil "anonim" durumudur.
        if (error instanceof ApiRequestError && error.code === "UNAUTHORIZED")
          return { user: null };
        throw error;
      }
    },
  });

  const user = sessionQuery.data?.user ?? null;

  /**
   * ⚠ Kimlik REF'te: seçim yazan geri çağırımlar (`switchWorkspace`, `recoverWorkspace`)
   * `user`'a bağımlı olsaydı her oturum yenilemesinde kimlikleri değişir ve onları
   * bağımlılık dizisinde taşıyan her şey yeniden kurulurdu.
   */
  const kullaniciRef = useRef<string | null>(null);
  kullaniciRef.current = user?.id ?? null;

  /**
   * HATIRLANAN SEÇİMİN SAHİBİ DENETLENİYOR.
   *
   * ⚠ Oturum sahibi değiştiyse önceki kullanıcının seçimi ATILIYOR: ortak bilgisayarda
   * ikinci kullanıcı, birincinin alanına düşmemeli. Kayıtta sahip yoksa (bu değişiklikten
   * önce yazılmış seçim) sahiplenip devam ediliyor — kullanıcıyı bir kereliğine seçim
   * ekranına atmak, çözmeye çalıştığımız rahatsızlığın ta kendisi olurdu.
   */
  useEffect(() => {
    if (!user) return;
    const { tenantId, ownerId } = hatirlananSecim();
    if (tenantId === null) return;
    if (ownerId === null) {
      try {
        localStorage.setItem(ACTIVE_TENANT_OWNER_KEY, user.id);
      } catch {
        /** yazılamazsa seçim yalnız bu sekmede yaşar */
      }
      setActiveTenantId((mevcut) => mevcut ?? tenantId);
      return;
    }
    if (ownerId !== user.id) {
      try {
        localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
        localStorage.removeItem(ACTIVE_TENANT_OWNER_KEY);
      } catch {
        /** yoksay */
      }
      setActiveTenantId(null);
      return;
    }
    /** Aynı kullanıcı: çıkış sonrası bellekten düşen seçim geri yükleniyor. */
    setActiveTenantId((mevcut) => mevcut ?? tenantId);
  }, [user]);

  const membershipsQuery = useQuery({
    queryKey: ["memberships"],
    enabled: user !== null,
    queryFn: async () => (await api<Membership[]>(AUTH_ROUTES.memberships)).data,
  });

  const memberships = useMemo(() => membershipsQuery.data ?? [], [membershipsQuery.data]);
  const activeTenant =
    memberships.find((membership) => membership.tenantId === activeTenantId) ?? null;

  /**
   * ⚠ SAHİP KİMLİĞİ SEÇİMLE BİRLİKTE yazılıyor. Ayrı ayrı yazılsalardı biri güncellenip
   * diğeri eskide kalabilir ve seçim yanlış kişiye ait görünürdü.
   */
  const setActiveTenant = useCallback((tenantId: string | null, ownerId?: string | null) => {
    setActiveTenantId(tenantId);
    try {
      if (tenantId) {
        localStorage.setItem(ACTIVE_TENANT_STORAGE_KEY, tenantId);
        if (ownerId) localStorage.setItem(ACTIVE_TENANT_OWNER_KEY, ownerId);
      } else {
        localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
        localStorage.removeItem(ACTIVE_TENANT_OWNER_KEY);
      }
    } catch {
      /** Depolama yazılamıyorsa seçim yalnız bu sekmede yaşar; akış kesilmiyor. */
    }
  }, []);

  /**
   * Aktif alanın bağlamı. Anahtar TENANT'A BAĞLI (`tenantKey`): alan değişince eski
   * bağlam bellekten atılıyor ve yenisi hedefin kendi verisiyle doğuyor. Tek bir
   * `["context"]` anahtarı kullanılsaydı iki alanın bağlamı aynı cache girdisinde
   * karışırdı.
   */
  const contextQuery = useQuery({
    queryKey: activeTenantId ? tenantKey(activeTenantId, "context") : ["t", "yok", "context"],
    enabled: user !== null && activeTenantId !== null,
    queryFn: async () =>
      (
        await api<WorkspaceContext>(`/api/tenants/${activeTenantId!}/context`, {
          tenantId: activeTenantId!,
        })
      ).data,
  });

  const activeContext = contextQuery.data ?? null;

  const can = useCallback(
    (permission: string) => activeContext?.permissions.includes(permission) ?? false,
    [activeContext],
  );

  /**
   * SON BAŞLATILAN GEÇİŞİN SIRA NUMARASI (CLOS-814).
   *
   * ⚠ Ölçüldü: koruma yokken üst üste iki geçiş bir yarışa dönüyordu. Kullanıcı büroya
   * tıklayıp beklemeden şirkete tıkladığında, büronun GEÇ dönen yanıtı en son yazan
   * oluyor ve kullanıcı şirkete tıkladığı hâlde büroda — müvekkil yetkileriyle — kalıyordu.
   * Yani yetenek kümesi kullanıcının seçiminden değil ağın hızından çıkıyordu.
   *
   * Sayaç `useRef` çünkü karar RENDER'A değil ÇAĞRIYA ait: state olsaydı iki çağrı aynı
   * render'ın kapanışını okur ve ikisi de kendini "en son" sanardı.
   */
  const gecisSirasi = useRef(0);

  const switchWorkspace = useCallback(
    async (tenantId: string) => {
      const buGecis = ++gecisSirasi.current;
      /**
       * 1) ÖNCE DOĞRULA. Sunucu üyeliği ve erişim durumunu yeniden değerlendiriyor;
       *    liste bayat olabilir (üyelik bu arada askıya alınmış olabilir). Doğrulama
       *    düşerse `api()` fırlatır ve aşağıdaki iki adım HİÇ çalışmaz — kullanıcı
       *    mevcut alanında kalır.
       */
      let data: WorkspaceContext;
      try {
        data = (await api<WorkspaceContext>(`/api/tenants/${tenantId}/context`, { tenantId })).data;
      } catch (error) {
        /**
         * Geçiş reddedildiyse LİSTE DE BAYAT: kullanıcı seçilebilir görünen bir satıra
         * tıkladı ama sunucu erişimi reddetti (üyelik bu arada askıya alınmış olabilir).
         * Listeyi tazelemezsek aynı satır seçilebilir kalır ve kullanıcı aynı duvara
         * tekrar tekrar toslar.
         *
         * ⚠ `refetchQueries`, `invalidateQueries` DEĞİL: invalidate yalnız bayat
         * işaretliyor ve çekimi gözlemciye bırakıyor — ölçüldü, bu koşulda çekim hiç
         * tetiklenmiyordu, yani tazeleme kâğıt üzerinde kalıyordu.
         */
        void queryClient.refetchQueries({ queryKey: ["memberships"] });
        throw error;
      }

      /**
       * 2) BU GEÇİŞ HÂLÂ EN SONU MU? Değilse sonucu UYGULAMIYORUZ (CLOS-814).
       *
       * ⚠ Yalnız `activeTenantId`'yi korumak yetmez: bağlam yazımı da atlanmalı. Aksi
       *    hâlde önbellekte eski hedefin bağlamı durur ve kullanıcı oraya bir daha
       *    geçtiğinde doğrulanmamış bir bağlamla açılırdı.
       *
       * ⚠ Veri yine DÖNÜYOR ama uygulanmıyor: çağıran bir hata görmemeli, çünkü ortada
       *    bir arıza yok — kullanıcı fikrini değiştirdi.
       */
      if (buGecis !== gecisSirasi.current) return data;

      /**
       * 3) Önceki alanın verisini AT. `queryClient.clear()` değil: o oturumu da siler ve
       *    geçiş çıkış yapılmış gibi görünürdü (kartın ilk kriteri bunu yasaklıyor).
       */
      clearTenantQueries(queryClient);

      // 4) Bağlamı yaz. Yeni alanın bağlamı bir sonraki render'da kendi anahtarıyla doğar.
      setActiveTenant(tenantId, kullaniciRef.current);
      queryClient.setQueryData(tenantKey(tenantId, "context"), data);
      return data;
    },
    [queryClient, setActiveTenant],
  );

  /**
   * Erişimi kaybedilmiş alandan GÜVENLİ GERİ DÖNÜŞ — DURUM tarafı (CLOS-81 `[044]`).
   *
   * Kararı `resolveSafeWorkspace` veriyor; burada yalnız uygulanıyor: eski alanın verisi
   * atılıyor ve yedek bağlam yazılıyor. İkisi tek yerde çünkü ayrı ayrı yapılsaydı biri
   * unutulur ve "eski bağlam yeni isteklere taşınmaz" kriteri sessizce ihlal edilirdi.
   *
   * ⚠ KULLANICIYA MESAJ BURADA GÖSTERİLMİYOR. İlk yazımda `useToast` buradaydı ve
   * `AuthProvider`'ı `ToastProvider`'a bağımlı hale getirdi — sekiz test "useToast,
   * ToastProvider içinde kullanılmalıdır" ile düştü. Oturum bağlamının bir bildirim
   * altyapısına bağlı olması yanlış yön; mesajı guard gösteriyor.
   */
  const recoverWorkspace = useCallback(
    (karar: Extract<SafeWorkspaceDecision, { tur: "yedek" }>) => {
      /**
       * ⚠ Kurtarma da bir geçiştir ve UÇAN geçişleri geçersiz kılar (CLOS-814). Sayacı
       * artırmasaydık, erişim kaybı yüzünden yedek alana düşen kullanıcı, o sırada yolda
       * olan bir geçişin yanıtıyla tekrar erişemediği alana taşınabilirdi.
       */
      gecisSirasi.current += 1;
      clearTenantQueries(queryClient);
      setActiveTenant(karar.tenantId, kullaniciRef.current);
    },
    [queryClient, setActiveTenant],
  );

  /**
   * OTURUM ORTASINDA gelen erişim kaybı (CLOS-81 `[044]`).
   *
   * Kartın çalışma zamanı koşulu erişimin *"her istek sınırında"* yeniden değerlendirilmesini
   * istiyor. Kullanıcı `/app`'te otururken üyeliği askıya alınabilir; o andan sonra her
   * çağrı 403 döner ve hiçbir katman bunu yakalamıyordu — ekran boş kalır ya da patlardı.
   *
   * Burada 403 görüldüğünde yapılan tek şey ÜYELİK LİSTESİNİ TAZELEMEK. Kararı yine
   * `RequireTenant` + `resolveSafeWorkspace` veriyor: liste güncellenince alan artık
   * `selectable` olmadığı için güvenli geri dönüş kendiliğinden çalışıyor. İkinci bir
   * karar noktası açsaydık iki yerin ayrışma riski doğardı.
   *
   * ⚠ `refetchQueries`, `invalidateQueries` DEĞİL: invalidate yalnız bayat işaretliyor ve
   * yeniden çekmeyi gözlemciye bırakıyor — ölçüldü, bu koşulda çekim tetiklenmiyordu.
   *
   * ⚠ Üyelik sorgusunun KENDİSİ 403 alırsa tazeleme yapılmıyor; yoksa sonsuz döngü olurdu.
   */
  useEffect(() => {
    return queryClient.getQueryCache().subscribe((olay) => {
      if (olay.type !== "updated" || olay.action.type !== "error") return;
      const hata = olay.action.error;
      if (!(hata instanceof ApiRequestError) || hata.code !== "FORBIDDEN") return;
      if (olay.query.queryKey[0] === "memberships") return;
      void queryClient.refetchQueries({ queryKey: ["memberships"] });
    });
  }, [queryClient]);

  const refreshSession = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["session"] });
    await queryClient.invalidateQueries({ queryKey: ["memberships"] });
  }, [queryClient]);

  /**
   * ⚠ Giriş İKİ SONUÇ döndürebiliyor: oturum ya da bir meydan okuma token'ı (`[018]`).
   * Yanıtı tek biçim sansaydık — ki öyle sayıyorduk — 2FA'sı açık kullanıcı için giriş
   * sessizce yarım kalırdı: yanıtta `user` yok, hata da yok, hiçbir şey olmuyor.
   */
  const login = useCallback(
    async (input: LoginInput): Promise<LoginSonucu> => {
      const { data } = await api<{
        user?: AuthUser;
        twoFactorRequired?: boolean;
        challenge?: string;
      }>(AUTH_ROUTES.login, { method: "POST", body: JSON.stringify(input) });
      if (data.twoFactorRequired && data.challenge) {
        return { durum: "ikinci-adim", challenge: data.challenge };
      }
      await refreshSession();
      return { durum: "acildi" };
    },
    [refreshSession],
  );

  /**
   * İkinci adım. Kod ve kurtarma kodu AYRI uçlara gidiyor: sunucu ikisini farklı
   * doğruluyor ve kurtarma kodu tek kullanımlık — tek uca yığmak o farkı gizlerdi.
   */
  const completeTwoFactor = useCallback(
    async (input: { challenge: string; code?: string; recoveryCode?: string }) => {
      const yol = input.recoveryCode ? AUTH_ROUTES.twoFactorRecovery : AUTH_ROUTES.twoFactor;
      const govde = input.recoveryCode
        ? { challenge: input.challenge, recoveryCode: input.recoveryCode }
        : { challenge: input.challenge, code: input.code };
      await api<{ user: AuthUser }>(yol, { method: "POST", body: JSON.stringify(govde) });
      await refreshSession();
    },
    [refreshSession],
  );

  const register = useCallback(
    async (input: RegisterInput) => {
      await api<{ user: AuthUser }>(AUTH_ROUTES.register, {
        method: "POST",
        body: JSON.stringify(input),
      });
      await refreshSession();
    },
    [refreshSession],
  );

  const logout = useCallback(async () => {
    await api<null>(AUTH_ROUTES.logout, { method: "DELETE" });
    /**
     * ⚠ SEÇİM SİLİNMİYOR, YALNIZ BELLEKTEN DÜŞÜYOR (saha bulgusu, 5 Eylül 2026).
     *
     * Eskiden çıkış hatırlanan alanı da siliyordu ve iki organizasyonu olan kullanıcı her
     * girişte seçim ekranıyla karşılaşıyordu. Korunmak istenen şey "başkasının alanına
     * düşmemek"ti; onu artık sahip kimliği sağlıyor — aşağıdaki etki, oturum sahibi
     * değiştiğinde seçimi kendisi siliyor. Kendi hesabıyla dönen kullanıcı bıraktığı
     * yerden devam ediyor.
     */
    setActiveTenantId(null);
    /**
     * ⚠ Tema önbelleği de siliniyor (CLOS-105 `[066]`): ortak bir bilgisayarda bir
     * sonraki kullanıcı öncekinin temasıyla karşılaşmamalı. Tema zararsız görünse de o
     * kişinin hesabından bir bilgidir — kartın *"sonraki oturumun bağlamına sızmaz"*
     * kriteri.
     */
    clearThemeCache();
    queryClient.clear();
    await queryClient.invalidateQueries({ queryKey: ["session"] });
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      memberships,
      isLoading: sessionQuery.isPending,
      // Oturum varsa üyelik sorgusu aktiftir; henüz veri gelmediyse yükleniyordur.
      // `isPending` enabled=false iken de true döner, bu yüzden user koşuluyla birlikte.
      membershipsLoading: user !== null && membershipsQuery.isPending,
      membershipsError: membershipsQuery.isError,
      activeTenantId,
      activeTenant,
      activeContext,
      can,
      setActiveTenant,
      switchWorkspace,
      recoverWorkspace,
      refreshSession,
      login,
      completeTwoFactor,
      register,
      logout,
    }),
    [
      user,
      memberships,
      sessionQuery.isPending,
      membershipsQuery.isPending,
      membershipsQuery.isError,
      activeTenantId,
      activeTenant,
      activeContext,
      can,
      setActiveTenant,
      switchWorkspace,
      recoverWorkspace,
      refreshSession,
      login,
      completeTwoFactor,
      register,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth, AuthProvider içinde kullanılmalıdır");
  return ctx;
}
